import React, { useState, useEffect, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Modal, Button, Form, Badge } from "react-bootstrap";
import { all_routes } from "../../router/all_routes";
import { toast } from "react-toastify";
import axios from "axios";
import {
  createCalendarEvent,
  getAllCalendarEvents,
  updateCalendarEvent,
  deleteCalendarEvent,
  CalendarEvent,
} from "../../../api/calander";
import { API_BASE_PATH } from "../../../environment";
import { formatDate } from "@fullcalendar/core";
import { useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import { fetchUsersByClientId } from "../../../api/todo";
import { validateCalendarForm,CalendarFormErrors } from "../../../api/applicationValidation";
import { time } from "echarts";
import { Link } from "react-router-dom";
import ScrollToTop from "../../../core/common/ScrollToTop";
import ModalDeleteEffect from "../../../core/common/ModalDeleteEffect";

// Define interfaces
interface CalendarEventType {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  calendar: string;
  attendees: string[];
  location?: string;
  reminder?: string;
  description?: string;
  is_company_event: boolean;
  visibility_type: "public" | "private_user" | "private_admin" | "restricted";
  repeat_reminder?: string;
  color?: string;
}

interface UserOption {
  value: string;
  label: string;
}

interface CalendarOption {
  name: string;
  color: string;
}

// Static fallback for calendar options if API fails
const DEFAULT_CALENDAR_OPTIONS: CalendarOption[] = [
  { name: "Work", color: "#7f8c8d" },
  { name: "Personal", color: "#ff0000" },
  { name: "Team", color: "#00ff00" },
];

const REMINDER_OPTIONS: string[] = [
  "No reminder",
  "5 minutes before",
  "15 minutes before",
  "30 minutes before",
  "60 minutes before",
];

const REPEAT_OPTIONS: string[] = [
  "none",
  "daily",
  "weekly",
  "monthly",
  "yearly",
];

// Helper to validate visibility_type
const validateVisibilityType = (visibility: string): "restricted" => {
  const validTypes = ["restricted"];
  return validTypes.includes(visibility)
    ? (visibility as "restricted")
    : "restricted"; // Default to restricted
};


const Calendar: React.FC = () => {
  const [events, setEvents] = useState<CalendarEventType[]>([]);
  const [eventTitle, setEventTitle] = useState<string>("");
  const [startDate, setStartDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [startTime, setStartTime] = useState<string>("09:00");
  const [endTime, setEndTime] = useState<string>("10:00");
  const [endDate, setEndDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [allDay, setAllDay] = useState<boolean>(false);
  const [calendar, setCalendar] = useState<string>("");
  const [attendees, setAttendees] = useState<string[]>([]);
  const [currentAttendeeInput, setCurrentAttendeeInput] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [reminder, setReminder] = useState<string>("No reminder");
  const [repeat, setRepeat] = useState<string>("none");
  const [description, setDescription] = useState<string>("");
  const userRole = useSelector((state: RootState) => state.auth.role);
  const route = all_routes;
  const [visibilityType, setVisibilityType] = useState<
    "public" | "private_user" | "private_admin" | "restricted"
  >("restricted");
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [currentViewingEvent, setCurrentViewingEvent] =
    useState<CalendarEventType | null>(null);
  const [showAddEventModal, setShowAddEventModal] = useState<boolean>(false);
  const [showViewEventModal, setShowViewEventModal] = useState<boolean>(false);
  const [showEditEventModal, setShowEditEventModal] = useState<boolean>(false);
  const [showCalendarDropdown, setShowCalendarDropdown] =
    useState<boolean>(false);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [calendarOptions, setCalendarOptions] = useState<CalendarOption[]>(
    DEFAULT_CALENDAR_OPTIONS
  );
const [isFormValid, setIsFormValid] = useState(false);
useEffect(() => {
  const styleId = "animated-badge-keyframes";
  if (!document.getElementById(styleId)) {
    const styleSheet = document.createElement("style");
    styleSheet.id = styleId;
    styleSheet.innerText = `
      @keyframes shine {
        0% {
          transform: skewX(-20deg) translateX(-150%);
        }
        50% {
           transform: skewX(-20deg) translateX(150%);
        }
        100% {
          transform: skewX(-20deg) translateX(150%);
        }
      }
    `;
    document.head.appendChild(styleSheet);
  }
}, []);

useEffect(() => {
  const validationErrors = validateCalendarForm({
    title: eventTitle,
    startDate,
    endDate,
    startTime,
    endTime,
    allDay,
    description,
    attendees,
    location,
    visibilityType,
  });
  setErrors(validationErrors);
  setIsFormValid(Object.keys(validationErrors).length === 0 && eventTitle.trim() !== "");
}, [
  eventTitle,
  startDate,
  endDate,
  startTime,
  endTime,
  allDay,
  description,
  attendees,
  location,
  visibilityType
]);
const [touched, setTouched] = useState({
  title: false,
  startDate: false,
  endDate: false,
  startTime: false,
  endTime: false,
  time: false,
  description: false,
  attendees: false,
  location: false,
});

const makeModalDraggable = (modalId: string) => {
  const modal = document.getElementById(modalId);
  if (!modal) return null;

  // Prevent duplicate initialization
  if (modal.dataset.draggableInit === 'true') return null;
  modal.dataset.draggableInit = 'true';

  const dialog = modal.querySelector('.modal-dialog') as HTMLElement | null;
  const header = modal.querySelector('.modal-header') as HTMLElement | null;

  if (!dialog || !header) return null;

  header.style.cursor = 'move';
  header.style.userSelect = 'none';

  let isDragging = false;
  let startX = 0;
  let startY = 0;
  let initialLeft = 0;
  let initialTop = 0;

  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    const newLeft = initialLeft + dx;
    const newTop = initialTop + dy;

    // Keep inside viewport
    const maxLeft = window.innerWidth - dialog.offsetWidth;
    const maxTop = window.innerHeight - dialog.offsetHeight;

    dialog.style.left = `${Math.max(0, Math.min(newLeft, maxLeft))}px`;
    dialog.style.top = `${Math.max(0, Math.min(newTop, maxTop))}px`;
  };

  const onMouseUp = () => {
    isDragging = false;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  const onMouseDown = (e: MouseEvent) => {
    // Skip if clicking close button, inputs, selects, etc.
    const target = e.target as HTMLElement;
    if (
      target.closest('.btn-close') ||
      target.closest('button:not(.btn-close)') ||
      target.closest('input') ||
      target.closest('select') ||
      target.closest('textarea')
    ) {
      return;
    }

    if (!header.contains(target)) return;

    e.preventDefault();

    const rect = dialog.getBoundingClientRect();

    isDragging = true;
    startX = e.clientX;
    startY = e.clientY;
    initialLeft = rect.left;
    initialTop = rect.top;

    // Prepare for dragging
    dialog.classList.remove('modal-dialog-centered');
    dialog.style.position = 'fixed';
    dialog.style.margin = '0';
    dialog.style.left = `${rect.left}px`;
    dialog.style.top = `${rect.top}px`;
    dialog.style.transform = 'none';

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const resetPosition = () => {
    dialog.style.position = '';
    dialog.style.left = '';
    dialog.style.top = '';
    dialog.style.margin = '';
    dialog.style.transform = '';
    if (!dialog.classList.contains('modal-dialog-centered')) {
      dialog.classList.add('modal-dialog-centered');
    }
    isDragging = false;
  };

  header.addEventListener('mousedown', onMouseDown);
  modal.addEventListener('hidden.bs.modal', resetPosition);

  // Return cleanup function
  return () => {
    header.removeEventListener('mousedown', onMouseDown);
    modal.removeEventListener('hidden.bs.modal', resetPosition);
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };
};

// ────────────────────────────────────────────────
//  Now inside your Calendar component, use this useEffect:
// ────────────────────────────────────────────────

useEffect(() => {
  const cleanups: Array<(() => void) | null> = [];

  // Add the modals you actually use in THIS component
  cleanups.push(makeModalDraggable("add_calendar"));     // ← Add/Edit modal
  cleanups.push(makeModalDraggable("view_event_modal")); // ← View modal

  // Only add others if they exist in THIS page/component
  // cleanups.push(makeModalDraggable("edit_calls"));
  // cleanups.push(makeModalDraggable("delete-note-units"));
  // etc.

  return () => {
    cleanups.forEach(cleanup => cleanup?.());
  };
}, []);   // ← important: empty deps = run once

  const [errors, setErrors] = useState<CalendarFormErrors>({});
const runValidation = () => {
  const validationErrors = validateCalendarForm({
    title: eventTitle,
    startDate,
    endDate,
    startTime,
    endTime,
    allDay,
    description,
    attendees,
    location,
    visibilityType,
  });

  setErrors(validationErrors);
  return Object.keys(validationErrors).length === 0;
};

  const calendarColors = [
    "#1abc9c",
    "#3498db",
    "#9b59b6",
    "#e67e22",
    "#e74c3c",
    "#2ecc71",
    "#f1c40f",
    "#34495e",
    "#7f8c8d",
  ];

  const getRandomColor = () => {
    return calendarColors[Math.floor(Math.random() * calendarColors.length)];
  };

  const colorMapRef = useRef(new Map()); // persists across re-renders

  const clientId = useSelector((state: RootState) => state.auth.client_id);

  // Fetch calendar options
  useEffect(() => {
    const fetchCalendarOptions = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_PATH}/calendars`, {
          headers: {
            Authorization: localStorage.getItem("authToken") || "",
            Accept: "application/json",
          },
        });
        setCalendarOptions(data);
        setCalendar(data[0]?.name || DEFAULT_CALENDAR_OPTIONS[0].name);
      } catch (error) {
        console.error("Failed to fetch calendar options:", error);
        setCalendarOptions(DEFAULT_CALENDAR_OPTIONS);
        setCalendar(DEFAULT_CALENDAR_OPTIONS[0].name);
      }
    };

    fetchCalendarOptions();
  }, []);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if(!clientId) return;
        const users = await fetchUsersByClientId(clientId || "");
        const options: UserOption[] = users.map((user: any) => ({
          value: user.id.toString(),
          label: user.name,
        }));
        setUserOptions(options);
      } catch (err) {
        console.error("Failed to fetch users:", err);
        console.error("Failed to load users.");
      }
    };

    if (clientId) fetchUsers();
  }, [clientId]);

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const events = await getAllCalendarEvents(true);
        setEvents(
          events.map((event: CalendarEvent) => {
            const calendarOption =
              calendarOptions.find(
                (cal) => cal.name === event.visibility_type
              ) ||
              calendarOptions[0] ||
              DEFAULT_CALENDAR_OPTIONS[0];
            return {
              id: event.id,
              title: event.title,
              start: new Date(event.event_start),
              end: new Date(event.event_end),
              allDay:
                !event.event_start.includes("T") ||
                !event.event_end.includes("T"),
              calendar: calendarOption.name,
              attendees: event.assigned_user_ids || [],
              location: event.location || "",
              reminder: event.reminder_minutes_before
                ? REMINDER_OPTIONS.find((opt) =>
                    opt.includes(`${event.reminder_minutes_before}`)
                  ) || `${event.reminder_minutes_before} minutes before`
                : "No reminder",
              repeat_reminder: event.repeat_reminder || "none",
              description: event.description || "",
              is_company_event: event.is_company_event ?? false,
              visibility_type: validateVisibilityType(event.visibility_type),
              color: calendarOption.color,
            };
          })
        );
      } catch (error) {
        console.error("Failed to fetch events:", error);
      }
    };
    if (calendarOptions.length) fetchEvents();
  }, [calendarOptions]);

  const resetFormFields = () => {
    setEventTitle("");
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setStartTime("09:00");
    setEndTime("10:00");
    setEndDate(today);
    setAllDay(false);
    setCalendar(calendarOptions[0]?.name || DEFAULT_CALENDAR_OPTIONS[0].name);
    setAttendees([]);
    setCurrentAttendeeInput("");
    setLocation("");
    setReminder("No reminder");
    setRepeat("none");
    setDescription("");
    setVisibilityType("restricted");
    setSelectedEventId(null);
    setCurrentViewingEvent(null);
    setErrors({});
    
  };

  const populateFormForEvent = (event: CalendarEventType) => {
    setEventTitle(event.title);
    setStartDate(event.start.toISOString().split("T")[0]);
    setStartTime(
      event.allDay
        ? "00:00"
        : event.start.toISOString().split("T")[1].slice(0, 5)
    );
    setEndDate(event.end.toISOString().split("T")[0]);
    setEndTime(
      event.allDay ? "23:59" : event.end.toISOString().split("T")[1].slice(0, 5)
    );
    setAllDay(event.allDay);
    setCalendar(event.calendar);
    setAttendees(event.attendees);
    setCurrentAttendeeInput("");
    setLocation(event.location || "");
    setReminder(event.reminder || "No reminder");
    setRepeat(event.repeat_reminder || "none");
    setDescription(event.description || "");
    setVisibilityType(event.visibility_type);
  };

  const handleDateSelect = (selectInfo: any) => {
    resetFormFields();
    setStartDate(selectInfo.startStr.split("T")[0]);
    let formEndDate = selectInfo.endStr;
    if (selectInfo.allDay) {
      const tempEndDate = new Date(selectInfo.endStr);
      tempEndDate.setDate(tempEndDate.getDate() - 1);
      formEndDate = tempEndDate.toISOString().split("T")[0];
    } else {
      formEndDate = selectInfo.endStr.split("T")[0];
    }
    setEndDate(formEndDate);
    setAllDay(selectInfo.allDay);
    if (!selectInfo.allDay) {
      setStartTime(selectInfo.startStr.substring(11, 16) || "09:00");
      setEndTime(selectInfo.endStr.substring(11, 16) || "10:00");
    }
    setShowAddEventModal(true);
  };

  const handleEventClick = (clickInfo: any) => {
    const eventId = clickInfo.event.id;
    const foundEvent = events.find((e) => e.id === eventId);
    if (foundEvent) {
      setSelectedEventId(foundEvent.id);
      setCurrentViewingEvent(foundEvent);
      populateFormForEvent(foundEvent);
      setShowViewEventModal(true);
    }
  };

  const handleCreateEvent = async () => {
    setTouched({
    title: true,
    startDate: true,
    endDate: true,
    startTime: true,
    endTime: true,
    time: true,
    description: true,
    attendees: true,
    location: true,
  });

  // Run full validation
  if (!runValidation() || eventTitle.trim() === "") {
    toast.error("Please fix the highlighted errors before creating the event");
    return;
  }
    try {
      
      const reminderMinutes = REMINDER_OPTIONS.includes(reminder)
        ? parseInt(reminder.match(/\d+/)?.[0] || "0")
        : 0;
      const eventData = {
        title: eventTitle,
        description,
        event_start: allDay
          ? `${startDate}T00:00:00`
          : `${startDate}T${startTime}:00`,
        event_end: allDay ? `${endDate}T23:59:59` : `${endDate}T${endTime}:00`,
        location,
        is_company_event: false, // Hardcode to false
        reminder_minutes_before: reminderMinutes,
        visibility_type: "restricted",
        repeat_reminder: repeat,
        assigned_user_ids: visibilityType === "restricted" ? attendees : [],
      };
      const newEvent = await createCalendarEvent(eventData);
      if (!newEvent) return;
      const calendarOption =
        calendarOptions.find((cal) => cal.name === calendar) ||
        calendarOptions[0] ||
        DEFAULT_CALENDAR_OPTIONS[0];
      setEvents([
        ...events,
        {
          id: newEvent.id,
          title: newEvent.title,
          start: new Date(newEvent.event_start),
          end: new Date(newEvent.event_end),
          allDay,
          calendar: calendarOption.name,
          attendees: newEvent.assigned_user_ids || [],
          location: newEvent.location || "",
          reminder: newEvent.reminder_minutes_before
            ? REMINDER_OPTIONS.find((opt) =>
                opt.includes(`${newEvent.reminder_minutes_before}`)
              ) || `${newEvent.reminder_minutes_before} minutes before`
            : "No reminder",
          repeat_reminder: newEvent.repeat_reminder || "none",
          description: newEvent.description || "",
          is_company_event: newEvent.is_company_event ?? false,
          visibility_type: validateVisibilityType(newEvent.visibility_type),
          color: calendarOption.color,
        },
      ]);
      commonModalClose();
    } catch (error) {
      console.error("Error creating event:", error);
    }
  };

  const handleEditEvent = async () => {
    if (!selectedEventId) return;

  setTouched({
    title: true,
    startDate: true,
    endDate: true,
    startTime: true,
    endTime: true,
    time: true,
    description: true,
    attendees: true,
    location: true,
  });

  if (!runValidation() || eventTitle.trim() === "") {
    toast.error("Please fix the highlighted errors before saving");
    return;
  }
    try {
      const reminderMinutes = REMINDER_OPTIONS.includes(reminder)
        ? parseInt(reminder.match(/\d+/)?.[0] || "0")
        : 0;
      const eventData = {
        title: eventTitle,
        description,
        event_start: allDay
          ? `${startDate}T00:00:00`
          : `${startDate}T${startTime}:00`,
        event_end: allDay ? `${endDate}T23:59:59` : `${endDate}T${endTime}:00`,
        location,
        is_company_event: false, // Hardcode to false
        reminder_minutes_before: reminderMinutes,
        visibility_type: "restricted",
        repeat_reminder: repeat,
        assigned_user_ids: visibilityType === "restricted" ? attendees : [],
      };
      const updatedEvent = await updateCalendarEvent(
        selectedEventId,
        eventData
      );
      if (!updatedEvent) return;
      const calendarOption =
        calendarOptions.find((cal) => cal.name === calendar) ||
        calendarOptions[0] ||
        DEFAULT_CALENDAR_OPTIONS[0];
      setEvents(
        events.map((event) =>
          event.id === updatedEvent.id
            ? {
                id: updatedEvent.id,
                title: updatedEvent.title,
                start: new Date(updatedEvent.event_start),
                end: new Date(updatedEvent.event_end),
                allDay,
                calendar: calendarOption.name,
                attendees: updatedEvent.assigned_user_ids || [],
                location: updatedEvent.location || "",
                reminder: updatedEvent.reminder_minutes_before
                  ? REMINDER_OPTIONS.find((opt) =>
                      opt.includes(`${updatedEvent.reminder_minutes_before}`)
                    ) ||
                    `${updatedEvent.reminder_minutes_before} minutes before`
                  : "No reminder",
                repeat_reminder: updatedEvent.repeat_reminder || "none",
                description: updatedEvent.description || "",
                is_company_event: updatedEvent.is_company_event ?? false,
                visibility_type: validateVisibilityType(
                  updatedEvent.visibility_type
                ),
                color: calendarOption.color,
              }
            : event
        )
      );
      commonModalClose();
    } catch (error) {
      console.error("Error updating event:", error);
    }
  };

  const handleDeleteEvent = async () => {
    if (!currentViewingEvent) return;
    try {
      await deleteCalendarEvent(currentViewingEvent.id);
      setEvents(events.filter((event) => event.id !== currentViewingEvent.id));
      commonModalClose();
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  const handleAddAttendee = () => {
    if (
      currentAttendeeInput &&
      !attendees.includes(currentAttendeeInput) &&
      userOptions.find((u) => u.value === currentAttendeeInput)
    ) {
      setAttendees([...attendees, currentAttendeeInput]);
      setTouched(t => ({ ...t, attendees: true }));
    }
    setCurrentAttendeeInput("");
  };

  const handleRemoveAttendee = (attendeeToRemove: string) => {
    setAttendees(attendees.filter((attendee) => attendee !== attendeeToRemove));
  };

  const commonModalClose = () => {
    setShowAddEventModal(false);
    setShowViewEventModal(false);
    setShowEditEventModal(false);
    resetFormFields();
  };

  const openAddModal = () => {
  resetFormFields(); // fresh start
  setShowAddEventModal(true);
};

  const openEditModalFromView = () => {
    if (!currentViewingEvent) return;
    setShowViewEventModal(false);
    setShowEditEventModal(true);
  };

  const handleEventUpdateFromCalendar = async (info: any) => {
    const { event } = info;
    try {
      const eventData = {
        title: event.title,
        description: event.extendedProps.description || "",
        event_start: formatDate(event.start, {
          month: "numeric",
          year: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
        }).replace(/\//g, "-").replace(",", ""), // Adjust format if needed
      };

      // FullCalendar's formatDate might not give exactly what the backend wants
      // Let's use a more robust way
      const formatToISO = (date: Date) => {
        const pad = (n: number) => n.toString().padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
      };

      const payload = {
        title: event.title,
        description: event.extendedProps.description || "",
        event_start: formatToISO(event.start),
        event_end: formatToISO(event.end || event.start),
        location: event.extendedProps.location || "",
        is_company_event: event.extendedProps.is_company_event || false,
        reminder_minutes_before: event.extendedProps.reminder
          ? parseInt(event.extendedProps.reminder.match(/\d+/)?.[0] || "0")
          : 0,
        visibility_type: "restricted",
        repeat_reminder: event.extendedProps.repeat_reminder || "none",
        assigned_user_ids: event.extendedProps.attendees || [],
      };

      await updateCalendarEvent(event.id, payload);

      setEvents((prev) =>
        prev.map((e) =>
          e.id === event.id
            ? {
                ...e,
                start: event.start,
                end: event.end || event.start,
                allDay: event.allDay,
              }
            : e
        )
      );
      toast.success("Event updated successfully");
    } catch (error) {
      console.error("Error updating event position:", error);
      info.revert();
    }
  };

  const handleEventDrop = (info: any) => {
    handleEventUpdateFromCalendar(info);
  };

  const handleEventResize = (info: any) => {
    handleEventUpdateFromCalendar(info);
  };

  const mappedEvents = events.map((event) => {
    if (!colorMapRef.current.has(event.id)) {
      colorMapRef.current.set(event.id, getRandomColor());
    }
    const color = colorMapRef.current.get(event.id);

    return {
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      allDay: event.allDay,
      backgroundColor: color,
      borderColor: color,
      extendedProps: {
        description: event.description,
        location: event.location,
        is_company_event: event.is_company_event,
        reminder: event.reminder,
        repeat_reminder: event.repeat_reminder,
        visibility_type: event.visibility_type,
        attendees: event.attendees,
      },
    };
  });

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header mb-0">
            <div className="row align-items-center w-100">
              <div className="col-lg-10 col-sm-14">
                <h3 className="page-title">
                  <i className="ti ti-calendar text-primary me-2" />Calendar</h3>
              </div>
              <div className="col-lg-2 col-sm-12 d-flex justify-content-end p-0">
                <Button
                  variant="primary"
                  onClick={() => {
                    resetFormFields();
                    setShowAddEventModal(true);
                  }}
                  style={{
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <span style={{ position: "relative", zIndex: 2 }}>Create Event</span>
                  <span
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      background: "linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)",
                      transform: "skewX(-20deg) translateX(-150%)",
                      animation: "shine 3s infinite",
                    }}
                  />
                </Button>
              </div>
            </div>
          </div>
           {/* Breadcrumb */}
              <nav aria-label="breadcrumb" className="page-header-breadcrumb mb-3">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link 
                      to={userRole === "admin" ? route.dealsDashboard : route.leadsDashboard}
                      style={{ 
                        color: "#9595b5", 
                        textDecoration: "none",
                        transition: "color 0.2s ease"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = "#E41F07"}
                      onMouseLeave={(e) => e.currentTarget.style.color = "#9595b5"}
                    >
                      Dashboard
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Calender
                  </li>
                </ol>
              </nav>
              {/* /Breadcrumb */}

          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card bg-white">
                  <div className="card-body p-0">
                    <FullCalendar
                      plugins={[
                        dayGridPlugin,
                        timeGridPlugin,
                        interactionPlugin,
                      ]}
                      headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right: "dayGridMonth,timeGridWeek,timeGridDay",
                      }}
                      initialView="dayGridMonth"
                      editable
                      selectable
                      selectMirror
                      dayMaxEvents
                      weekends={true}
                      events={mappedEvents}
                      select={handleDateSelect}
                      eventClick={handleEventClick}
                      eventContent={(arg) => {
                        const start = arg.event.start;
                        const end = arg.event.end;

                        const dateTime = start
                          ? formatDate(start, {
                              hour: "2-digit",
                              minute: "2-digit",
                              month: "short",
                              day: "numeric",
                            }) +
                            (end
                              ? ` - ${formatDate(end, {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}`
                              : "")
                          : "";

                        return (
                          <div
                            style={{
                              backgroundColor: arg.event.backgroundColor,
                              padding: "6px 10px",
                              borderRadius: "5px",
                              color: "#fff",
                              fontWeight: "600",
                              fontSize: "14px",
                              whiteSpace: "normal",
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px",
                            }}
                            title={`${arg.event.title} | ${dateTime}`}
                          >
                            <div>{arg.event.title}</div>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: "400",
                                opacity: 0.85,
                              }}
                            >
                              {dateTime}
                            </div>
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Event Modal */}
      <Modal
        id="add_calendar"  
        show={showAddEventModal || showEditEventModal}
        onHide={commonModalClose}
        centered
        className="effect-modal-show"
      >
        <Modal.Header className="d-flex align-items-center justify-content-between">
    <h5 className="modal-title text-primary fw-bold d-flex align-items-center mb-0">
      {showEditEventModal ? (
        <>
          <i className="ti ti-edit me-2 fs-20" />
          Edit Event
        </>
      ) : (
        <>
          <i className="ti ti-square-rounded-plus-filled me-2 fs-20" />
          Create New Event
        </>
      )}
    </h5>

    <button
      className="btn-close custom-btn-close border p-1 me-0 text-dark"
      onClick={commonModalClose}
    >
      <i className="ti ti-x" />
    </button>
  </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              placeholder="Event name"
              value={eventTitle}
              onChange={(e) => setEventTitle(e.target.value)}
  onBlur={() => setTouched(t => ({ ...t, title: true }))}
  isInvalid={touched.title && !!errors.title}
            />
            <Form.Control.Feedback type="invalid">
  {touched.title && errors.title}
</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3 d-flex align-items-center">
            <Form.Label className="me-3" style={{ width: 80, marginBottom: 0 }}>
              Calendar:
            </Form.Label>
            <div className="position-relative flex-grow-1">
              <div
                className="form-control d-flex align-items-center"
                onClick={() => setShowCalendarDropdown(!showCalendarDropdown)}
                style={{ cursor: "pointer" }}
              >
                {calendar && (
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      background: calendarOptions.find(
                        (c) => c.name === calendar
                      )?.color,
                      marginRight: 8,
                      borderRadius: 2,
                    }}
                  />
                )}
                {calendar || "Select Calendar"}
              </div>
              {showCalendarDropdown && (
                <div
                  className="position-absolute bg-white border rounded shadow-sm w-100"
                  style={{ top: "100%", left: 0, zIndex: 1056 }}
                >
                  {calendarOptions.map((cal) => (
                    <div
                      key={cal.name}
                      className="p-2 d-flex align-items-center"
                      onClick={() => {
                        setCalendar(cal.name);
                        setShowCalendarDropdown(false);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          background: cal.color,
                          marginRight: 8,
                          borderRadius: 2,
                        }}
                      />
                      {cal.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Form.Group>

          <div className="mb-3 d-flex flex-column flex-md-row flex-wrap align-items-center gap-3">
            {/* Start Date & Time */}
            <div className="d-flex flex-column">
              <label className="mb-1 fw-semibold">Start</label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, startDate: true }))}
  isInvalid={touched.startDate && !!errors.startDate}
                />
                <Form.Control
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, time: true }))}
                  disabled={allDay}
  isInvalid={touched.time && !!errors.time}
                />
                
              </div>
              {errors.startDate && (
      <div className="text-danger small mt-1">
        {errors.startDate}
      </div>
    )}
            </div>

            {/* End Date & Time */}
            <div className="d-flex flex-column">
              <label className="mb-1 fw-semibold">End</label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, endDate: true }))}
  isInvalid={touched.endDate && !!errors.endDate}
                />
                <Form.Control
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, time: true }))}
                  disabled={allDay}
  isInvalid={touched.time && !!errors.time}
                />  
              </div>
              {errors.endDate && (
      <div className="text-danger small mt-1">
        {errors.endDate}
      </div>
    )}
    {!allDay && touched.time && errors.time && (
  <div className="text-danger small w-100">
    {errors.time}
  </div>
)}
            </div>

            {/* All Day Toggle */}
            <Form.Check
              type="checkbox"
              label="All Day"
              className="align-self-start mt-md-4"
              checked={allDay}
              onChange={(e) => {
                const checked = e.target.checked;
                setAllDay(checked);
                if (checked) {
                  setStartTime("00:00");
                  setEndTime("23:59");
                }
              }}
            />
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Description:</Form.Label>
            <Form.Control
              as="textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => setTouched(t => ({ ...t, description: true }))}
  isInvalid={touched.description && !!errors.description}
            />
            {touched.description && errors.description && (
  <div className="text-danger small mt-2">
    {errors.description}
  </div>
)}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Attendees:</Form.Label>
            <div className="d-flex mb-2">
              <Form.Select
                value={currentAttendeeInput}
                onChange={(e) => {
    setCurrentAttendeeInput(e.target.value);
    setTouched(t => ({ ...t, attendees: true }));
  }}
  isInvalid={touched.attendees && !!errors.attendees}
              >
                <option value="">Select user</option>
                {userOptions.map((user) => (
                  <option key={user.value} value={user.value}>
                    {user.label}
                  </option>
                ))}
              </Form.Select>

              <Button onClick={handleAddAttendee}>Add Attendee</Button>
                        </div>
            {touched.attendees && errors.attendees && (
  <div className="text-danger small mt-2">
    {errors.attendees}
  </div>
)}

            
            <div className="d-flex flex-wrap" style={{ gap: "0.5rem" }}>
              {attendees.map((attendee, idx) => (
                <Badge
                  key={idx}
                  pill
                  bg="secondary"
                  className="d-flex align-items-center"
                >
                  {userOptions.find((u) => u.value === attendee)?.label ||
                    attendee}
                  <Button
                    variant="close"
                    className="ms-1 p-0"
                    style={{ fontSize: "0.75em", color: "white" }}
                    onClick={() => handleRemoveAttendee(attendee)}
                  />
                </Badge>
              ))}
            </div>
          </Form.Group>

          <Form.Group className="mb-3 d-flex align-items-center">
            <Form.Label className="me-3" style={{ width: 80, marginBottom: 0 }}>
              Location:
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              onBlur={() => setTouched(t => ({ ...t, location: true }))}
  isInvalid={touched.location && !!errors.location}
            />
          </Form.Group>
          {touched.location && errors.location && (
  <div className="text-danger small mt-2">
    {errors.location}
  </div>
)}

          <Form.Group className="mb-3 d-flex align-items-center">
            <Form.Label className="me-3" style={{ width: 80, marginBottom: 0 }}>
              Reminder:
            </Form.Label>
            <Form.Select
              value={reminder}
              onChange={(e) => setReminder(e.target.value)}
            >
              {REMINDER_OPTIONS.map((opt) => (
                <option key={opt}>{opt}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3 d-flex align-items-center">
            <Form.Label className="me-3" style={{ width: 80, marginBottom: 0 }}>
              Repeat:
            </Form.Label>
            <Form.Select
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            >
              {REPEAT_OPTIONS.map((opt) => (
                <option key={opt}>{opt}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* <Form.Group className="mb-3 d-flex align-items-center">
            <Form.Label className="me-3" style={{ width: 80, marginBottom: 0 }}>
              Visibility:
            </Form.Label>
            <Form.Select
              value={visibilityType}
              onChange={(e) =>
                setVisibilityType(
                  e.target.value as "public" | "private_user" | "private_admin" | "restricted"
                )
              }
            >
              <option value="public">Public</option>
              <option value="private_user">Private (User)</option>
              <option value="private_admin">Private (Admin)</option>
              <option value="restricted">Restricted</option>
            </Form.Select>
          </Form.Group> */}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={commonModalClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={showEditEventModal ? handleEditEvent : handleCreateEvent}
            disabled={!isFormValid}
          >
            {showEditEventModal ? "Save Changes" : "Create Event"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Event Modal */}
      <Modal 
        show={showViewEventModal} 
        onHide={commonModalClose} 
        centered
        id="view_event_modal"
        className="effect-modal-show"
      >
        <Modal.Header closeButton>
          <Modal.Title>Event Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentViewingEvent ? (
            <>
              <h4>{currentViewingEvent.title}</h4>
              <p>
                <strong>Calendar:</strong> {currentViewingEvent.calendar}
              </p>
              <p>
                <strong>Time:</strong>{" "}
                {new Date(currentViewingEvent.start).toLocaleString()} –{" "}
                {new Date(currentViewingEvent.end).toLocaleString()}
              </p>
              {currentViewingEvent.allDay && (
                <p>
                  <strong>(All day event)</strong>
                </p>
              )}
              {currentViewingEvent.location && (
                <p>
                  <strong>Location:</strong> {currentViewingEvent.location}
                </p>
              )}
              {currentViewingEvent.attendees?.length > 0 && (
                <p>
                  <strong>Attendees:</strong>{" "}
                  {currentViewingEvent.attendees
                    .map(
                      (id) =>
                        userOptions.find((u) => u.value === id)?.label || id
                    )
                    .join(", ")}
                </p>
              )}
              {currentViewingEvent.reminder && (
                <p>
                  <strong>Reminder:</strong> {currentViewingEvent.reminder}
                </p>
              )}
              {currentViewingEvent.repeat_reminder && (
                <p>
                  <strong>Repeat:</strong> {currentViewingEvent.repeat_reminder}
                </p>
              )}
              {currentViewingEvent.description && (
                <p>
                  <strong>Description:</strong>{" "}
                  {currentViewingEvent.description}
                </p>
              )}
              <p>
                <strong>Visibility:</strong>{" "}
                {currentViewingEvent.visibility_type}
              </p>
            </>
          ) : (
            <p>No event selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={commonModalClose}>
            Close
          </Button>
          <Button
            variant="danger"
            className="me-auto"
            onClick={handleDeleteEvent}
          >
            Delete
          </Button>
          <Button variant="primary" onClick={openEditModalFromView}>
            Edit
          </Button>
        </Modal.Footer>
      </Modal>
      <ModalDeleteEffect />
      <ScrollToTop />
    </>
  );
};

export default Calendar;
